import StaffNavigation from './StaffNavigation';
import InstructorNavigation from './InstructorNavigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useAuth } from '../../auth/useAuth';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigationScope } from '../../navigation';
import { EmployeeThemeProvider, useEmployeeTheme } from '../employee/EmployeeThemeContext';
import { CrmSidebarContext, CrmShellPresentationContext } from './CrmSidebarContext';
import { isManagementShellPilot } from './crmShellPilot';
import { resolveCrmShell } from './crmShellAdapters';
import { useCrmScrollTarget } from './useCrmScrollTarget';
import './crm-shell-pilot.css';
import GlobalHybridHeader from './GlobalHybridHeader';
import type { HybridHeaderProps } from './GlobalHybridHeader';
import { isCrmHeaderRoute } from './crmViews';
import './global-hybrid-header.css';
import './crm-brand-theme.css';
import './crm-navigation-theme.css';
import './crm-content-theme.css';
import '../../pages/accountant/accountant-theme.css';
import { crmPalettes, crmCssVariables, crmTypography } from './crmPalette';

function Frame({ children, header }: { children: ReactNode; header?: HybridHeaderProps }) {
  const { isDark } = useEmployeeTheme();
  const { language } = useLanguage();
  const palette = isDark ? crmPalettes.dark : crmPalettes.light;
  const [sidebar, setSidebar] = useState<(() => void) | null>(null);
  const registerSidebar = useCallback((toggle: (() => void) | null) => setSidebar(() => toggle), []);
  const [openHelp, setOpenHelp] = useState<(() => void) | null>(null);
  const registerHelp = useCallback((open: () => void) => {
    setOpenHelp(() => open);
    return () => setOpenHelp(current => current === open ? null : current);
  }, []);
  const location = useLocation();
  const { basename } = useNavigationScope();
  const adapter = resolveCrmShell(basename, location.pathname, header?.summer);
  const pilot = Boolean(adapter) || isManagementShellPilot(basename, location.pathname);
  const [scrollTarget, registerScrollTarget] = useState<HTMLElement | null>(null);
  const observedScrollTarget = useCrmScrollTarget(pilot, location.key);
  const homePath = adapter?.homePath ?? '/';
  const shellPresentation = useMemo(() => ({ pilot, scrollTarget: scrollTarget ?? observedScrollTarget, registerScrollTarget, homePath, hasSidebar: Boolean(sidebar), openHelp: openHelp ?? undefined, registerHelp }), [pilot, scrollTarget, observedScrollTarget, homePath, sidebar, openHelp, registerHelp]);
  const standaloneEmployee = (basename === '/app' || basename === '/') && ['/settings', '/employee/attendance', '/employee/mail', '/employee/instructor-payroll', '/mail'].includes(location.pathname);
  const standaloneInstructor = (basename === '/app' || basename === '/') && (location.pathname === '/instructor/payroll' || /^\/instructor\/groups\/[^/]+\/live$/.test(location.pathname));
  const muiTheme = useMemo(() => createTheme({ palette: {
    mode: isDark ? 'dark' : 'light', primary: { main: palette.primary, contrastText: palette['on-primary'] }, secondary: { main: palette.secondary, contrastText: palette['on-secondary'] },
    background: { default: palette.bg, paper: palette.surface },
    text: { primary: palette.text, secondary: palette.muted }, divider: palette.border,
    success: { main: palette.success }, warning: { main: palette.warning }, error: { main: palette.danger }, info: { main: palette.primary },
  }, direction: language === 'ar' ? 'rtl' : 'ltr', typography: { fontFamily: crmTypography[language] },
    components: {
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiTableCell: { styleOverrides: { head: { backgroundColor: palette['table-head'], color: palette.text } } },
      MuiOutlinedInput: { styleOverrides: { root: { backgroundColor: palette.input } } },
      MuiMenuItem: { styleOverrides: { root: { '&:hover': { backgroundColor: palette.hover }, '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: palette.active } } } },
      MuiChip: { styleOverrides: { root: ({ ownerState }) => ownerState.color === 'error' ? {} : { backgroundColor: palette.badge, color: palette['badge-text'], borderColor: palette.border } } },
      MuiTypography: { styleOverrides: { root: ({ ownerState }) => {
      const textColors: Record<string, string> = { primary: palette.accent, 'primary.main': palette.accent, 'info.main': palette.accent,
        'success.main': palette['success-text'], 'warning.main': palette['warning-text'], 'error.main': palette['danger-text'] };
      return ownerState.color && textColors[ownerState.color] ? { color: textColors[ownerState.color] } : {};
    } } } },
  }), [isDark, language, palette]);
  useEffect(() => {
    document.body.classList.add('crm-theme-active');
    document.body.classList.toggle('crm-dark-active', isDark);
    const variables = Object.entries(crmCssVariables(palette));
    const previous = variables.map(([name]) => [name, document.body.style.getPropertyValue(name)]);
    variables.forEach(([name, value]) => document.body.style.setProperty(name, String(value)));
    return () => { document.body.classList.remove('crm-theme-active', 'crm-dark-active'); previous.forEach(([name, value]) => value ? document.body.style.setProperty(name, value) : document.body.style.removeProperty(name)); };
  }, [isDark, palette]);
  return <CrmShellPresentationContext.Provider value={shellPresentation}><CrmSidebarContext.Provider value={registerSidebar}>
    <ConfigProvider direction={language === 'ar' ? 'rtl' : 'ltr'} theme={{ algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: { colorPrimary: palette.primary, colorLink: palette.accent, colorBgContainer: palette.surface, colorBgElevated: palette.elevated, colorBgLayout: palette.bg,
        colorText: palette.text, colorTextSecondary: palette.muted, colorTextLightSolid: palette['on-primary'], colorBorder: palette.border, colorBorderSecondary: palette.border,
        colorSuccess: palette.success, colorWarning: palette.warning, colorError: palette.danger, colorInfo: palette.primary,
        controlItemBgHover: palette.hover, controlItemBgActive: palette.active, controlItemBgActiveHover: palette.active },
      components: {
        Table: { headerBg: palette['table-head'], headerColor: palette.text, colorBgContainer: palette.table, rowHoverBg: palette.hover, rowSelectedBg: palette.active, rowSelectedHoverBg: palette.active },
        Input: { colorBgContainer: palette.input }, InputNumber: { colorBgContainer: palette.input }, DatePicker: { colorBgContainer: palette.input },
        Select: { colorBgContainer: palette.input, optionActiveBg: palette.hover, optionSelectedBg: palette.active, optionSelectedColor: palette.text },
        Tag: { defaultBg: palette.badge, defaultColor: palette['badge-text'] },
      } }}>
      <ThemeProvider theme={muiTheme}>
        <div className={`crm-application ${isDark ? 'crm-dark' : 'crm-light'}`} data-crm-shell-pilot={pilot ? (adapter?.section === 'manager' ? 'management' : adapter?.section ?? 'management') : undefined} data-crm-section={basename === '/accountant' ? 'accountant' : undefined} style={crmCssVariables(palette)} dir={language === 'ar' ? 'rtl' : 'ltr'}>
          <GlobalHybridHeader onToggleSidebar={() => sidebar?.()} {...header} />

          <div className="crm-workspace">{standaloneEmployee && <StaffNavigation />}{standaloneInstructor && <InstructorNavigation />}{children}</div>
        </div>
      </ThemeProvider>
    </ConfigProvider>
  </CrmSidebarContext.Provider></CrmShellPresentationContext.Provider>;
}

/** Summer staff has a separate authenticated account/session; reuse the visual component. */
export function SummerCrmFrame({ children, ...header }: HybridHeaderProps & { children: ReactNode }) {
  return <EmployeeThemeProvider><Frame header={header}>{children}</Frame></EmployeeThemeProvider>;
}

export default function CrmApplicationFrame({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { basename } = useNavigationScope();
  const location = useLocation();
  // The isolated summer staff session owns its shared frame; student/public pages keep their own shell.
  if (location.pathname.startsWith('/summer-training/')) return <>{children}</>;
  if (!isCrmHeaderRoute(basename, location.pathname, user)) return <>{children}</>;
  return <EmployeeThemeProvider><Frame>{children}</Frame></EmployeeThemeProvider>;
}
